import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tr from "date-fns/locale/tr";
import AdminLayout from "../../layouts/AdminLayout";

registerLocale("tr", tr);

function ReservationManagement() {
    const [reservations, setReservations] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("Tümü");
    const [dateFilter, setDateFilter] = useState({
        startDate: "",
        endDate: "",
    });
    const [showDatePicker, setShowDatePicker] = useState(false);
    const navigate = useNavigate();

    const [newReservation, setNewReservation] = useState({
        yachtId: "",
        yachtName: "",
        startDate: "",
        endDate: "",
        notes: "",
    });

    const [availableYachts, setAvailableYachts] = useState([]);
    const [loadingYachts, setLoadingYachts] = useState(false);

    const [notification, setNotification] = useState({
        show: false,
        type: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Kullanıcıya ait yat listesini çek
    const fetchYachts = useCallback(async () => {
        setLoadingYachts(true);
        try {
            // localStorage'dan token'ı al
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                setNotification({
                    show: true,
                    type: "error",
                    message: "Giriş yapmanız gerekiyor. Lütfen tekrar giriş yapın.",
                });
                setLoadingYachts(false);
                return;
            }

            const response = await fetch("/rest/yacht/getYachtListByUserId", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setNotification({
                        show: true,
                        type: "error",
                        message: "Oturumunuzun süresi dolmuş. Lütfen tekrar giriş yapın.",
                    });
                    // Kullanıcıyı login sayfasına yönlendir
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 2000);
                    return;
                }
                throw new Error('Kullanıcı yatları getirilemedi');
            }

            const data = await response.json();
            console.log("API'den gelen kullanıcı yat verileri:", data);
            
            // API'den gelen veriyi kontrol et - direkt array döndürüyor
            if (Array.isArray(data)) {
                setAvailableYachts(data);
                console.log(`${data.length} adet kullanıcı yatı rezervasyon için hazır`);
            } else {
                console.warn("Beklenmeyen veri formatı:", data);
                setAvailableYachts([]);
            }

        } catch (error) {
            console.error("Kullanıcı yatları getirme hatası:", error);
            setNotification({
                show: true,
                type: "error",
                message: `Yat listesi yüklenirken hata oluştu: ${error.message}`,
            });
            setAvailableYachts([]);
        } finally {
            setLoadingYachts(false);
        }
    }, []);

    // Tüm yatların müsaitliklerini çek
    const fetchYachtAvailabilities = useCallback(async (yachts) => {
        const token = localStorage.getItem("authToken");
        if (!token || !yachts.length) {
            return [];
        }

        try {
            console.log("Yat müsaitlikleri çekiliyor...", yachts.length, "yat için");
            
            // Her yat için müsaitlik verilerini paralel olarak çek
            const availabilityPromises = yachts.map(async (yacht) => {
                try {
                    const response = await fetch(`/rest/api/yachtavailability/yacht/${yacht.id}`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        console.warn(`Yat ${yacht.id} için müsaitlik getirilemedi: ${response.status}`);
                        return [];
                    }

                    const availabilities = await response.json();
                    console.log(`Yat ${yacht.id} (${yacht.name}) için ${availabilities.length} müsaitlik bulundu`);
                    
                    // Her müsaitliğe yat bilgilerini ekle
                    return availabilities.map(availability => ({
                        ...availability,
                        yacht: yacht,
                        yachtName: yacht.name,
                        captain: yacht.captain
                    }));
                } catch (error) {
                    console.error(`Yat ${yacht.id} müsaitlik hatası:`, error);
                    return [];
                }
            });

            // Tüm sonuçları bekle ve birleştir
            const allAvailabilities = await Promise.all(availabilityPromises);
            const flattenedAvailabilities = allAvailabilities.flat();
            
            console.log(`Toplam ${flattenedAvailabilities.length} müsaitlik kaydı bulundu`);
            return flattenedAvailabilities;

        } catch (error) {
            console.error("Yat müsaitlikleri çekme hatası:", error);
            return [];
        }
    }, []);

    // Tarih formatlama fonksiyonu
    const formatDate = (date) => {
        if (!date) return "";

        const days = [
            "Pazar",
            "Pazartesi",
            "Salı",
            "Çarşamba",
            "Perşembe",
            "Cuma",
            "Cumartesi",
        ];
        const months = [
            "Ocak",
            "Şubat",
            "Mart",
            "Nisan",
            "Mayıs",
            "Haziran",
            "Temmuz",
            "Ağustos",
            "Eylül",
            "Ekim",
            "Kasım",
            "Aralık",
        ];

        const day = days[date.getDay()];
        const month = months[date.getMonth()];

        return `${date.getDate()} ${month}, ${day}`;
    };

    // Yat müsaitliklerini çek ve rezervasyon formatına çevir
    const fetchReservations = useCallback(async () => {
        setLoading(true);
        try {
            // localStorage'dan token al
            const token = localStorage.getItem("authToken");

            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            // Önce kullanıcının yatlarını çek
            const yachtsResponse = await fetch("/rest/yacht/getYachtListByUserId", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!yachtsResponse.ok) {
                if (yachtsResponse.status === 401) {
                    throw new Error("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");
                }
                throw new Error("Kullanıcı yatları getirilemedi");
            }

            const yachtsData = await yachtsResponse.json();
            console.log("Yat listesi alındı:", yachtsData.length, "yat");

            // Yat müsaitliklerini çek
            const availabilities = await fetchYachtAvailabilities(yachtsData);
            
            // Müsaitlikleri rezervasyon formatına çevir
            const formattedReservations = availabilities.map((availability) => ({
                id: availability.id,
                yachtId: availability.yachtId,
                yachtName: availability.yachtName || "Bilinmeyen Yat",
                captain: availability.captain || availability.yacht?.captain || "Atanacak",
                customerName: null, // Müsaitlik kaydı, henüz müşteri yok
                customerEmail: null,
                startDate: availability.availableFrom ? availability.availableFrom.split('T')[0] : "",
                endDate: availability.availableTo ? availability.availableTo.split('T')[0] : "",
                status: "Müsait", // Müsaitlik kaydı olduğu için durum "Müsait"
                totalPrice: 0, // Müsaitlik kaydında fiyat yok
                notes: availability.note || "",
                createdAt: availability.createdAt,
                updatedAt: availability.updatedAt,
                // Orijinal availability verisini sakla
                originalAvailability: availability
            }));

            console.log(`${formattedReservations.length} müsaitlik kaydı rezervasyon formatına çevrildi`);
            setReservations(formattedReservations);
            setError(null);

        } catch (error) {
            console.error("Müsaitlikler yüklenirken hata:", error);
            setError(error.message);

            // Hata durumunda boş liste göster
            setReservations([]);

            setNotification({
                show: true,
                type: "error",
                message: `Müsaitlikler yüklenirken hata oluştu: ${error.message}`,
            });

            // Eğer token hatası ise login sayfasına yönlendir
            if (error.message.includes("Oturum süresi dolmuş")) {
                setTimeout(() => {
                    navigate("/login");
                }, 2000);
            }
        } finally {
            setLoading(false);
        }
    }, [fetchYachtAvailabilities, navigate]);

    // Component mount olduğunda yat listesi ve rezervasyonları çek
    useEffect(() => {
        // Debug: Token kontrolü
        const token = localStorage.getItem("authToken");
        console.log("ReservationManagement - Token var mı?", !!token);
        if (token) {
            console.log("Token değeri:", token.substring(0, 20) + "...");
        }

        fetchYachts();
        fetchReservations();
    }, [fetchYachts, fetchReservations]);

    // Yat listesi güncellendiğinde rezervasyonları yeniden çek
    useEffect(() => {
        if (availableYachts.length > 0) {
            console.log("Yat listesi güncellendi, müsaitlikler yeniden çekiliyor...");
            // fetchReservations zaten yatları çekip müsaitlikleri alıyor,
            // bu yüzden sadece yat listesi değiştiğinde çağırmaya gerek yok
        }
    }, [availableYachts]);

    // Modal açıkken body scroll'unu kapat
    useEffect(() => {
        if (showAddForm || showDatePicker) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showAddForm, showDatePicker]);

    // ESC tuşu ile modalları kapat
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showAddForm) {
                    setShowAddForm(false);
                }
                if (showDatePicker) {
                    setShowDatePicker(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showAddForm, showDatePicker]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewReservation({
            ...newReservation,
            [name]: value,
        });

        // Hata mesajını temizle
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: "",
            });
        }
    };

    // Yat seçimi fonksiyonu
    const handleYachtSelect = (yacht) => {
        setNewReservation({
            ...newReservation,
            yachtId: yacht.id.toString(), // String'e çevir tutarlılık için
            yachtName: yacht.name,
        });

        // Hata mesajını temizle
        if (formErrors.yachtId) {
            setFormErrors({
                ...formErrors,
                yachtId: "",
            });
        }
    };

    // Yat tipini sayıdan metne çeviren fonksiyon
    const getYachtTypeText = (typeNumber) => {
        const types = {
            1: "Motor Yacht",
            2: "Sailing Yacht", 
            3: "Catamaran",
            4: "Luxury Yacht",
            5: "Super Yacht"
        };
        return types[typeNumber] || "Bilinmeyen Tip";
    };

    // Seçilen yatın dolu tarihlerini hesapla
    const getExcludedDates = () => {
        if (!newReservation.yachtId) return [];

        const excludedDates = [];
        const yachtReservations = reservations.filter(
            (res) =>
                res.yachtName === newReservation.yachtName &&
                res.status !== "İptal"
        );

        yachtReservations.forEach((reservation) => {
            const startDate = new Date(reservation.startDate);
            const endDate = new Date(reservation.endDate);

            // Başlangıç ve bitiş tarihleri arasındaki tüm günleri ekle
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                excludedDates.push(new Date(currentDate));
                currentDate.setDate(currentDate.getDate() + 1);
            }
        });

        return excludedDates;
    };

    // Form validasyonu - Sadece gerekli alanlar
    const validateForm = () => {
        const errors = {};

        if (!newReservation.yachtId) {
            errors.yachtId = "Yat seçimi gereklidir";
        }

        if (!newReservation.startDate) {
            errors.startDate = "Başlangıç tarihi gereklidir";
        }

        if (!newReservation.endDate) {
            errors.endDate = "Bitiş tarihi gereklidir";
        }

        if (
            newReservation.startDate &&
            newReservation.endDate &&
            newReservation.startDate >= newReservation.endDate
        ) {
            errors.endDate =
                "Bitiş tarihi başlangıç tarihinden sonra olmalıdır";
        }

        if (!newReservation.notes || newReservation.notes.trim() === "") {
            errors.notes = "Notlar alanı boş bırakılamaz";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setNotification({
                show: true,
                type: "error",
                message: "Lütfen tüm gerekli alanları doldurun",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // localStorage'dan token al
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                setNotification({
                    show: true,
                    type: "error",
                    message: "Giriş yapmanız gerekiyor. Lütfen tekrar giriş yapın.",
                });
                setIsSubmitting(false);
                return;
            }

            // API'ye gönderilecek veriyi hazırla
            const requestBody = {
                yachtId: parseInt(newReservation.yachtId),
                availableFrom: `${newReservation.startDate}T10:00:00`,
                availableTo: `${newReservation.endDate}T18:00:00`,
                note: newReservation.notes.trim()
            };

            console.log("API'ye gönderilecek veri:", requestBody);

            // API çağrısı yap
            const response = await fetch("/rest/api/yachtavailability", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setNotification({
                        show: true,
                        type: "error",
                        message: "Oturumunuzun süresi dolmuş. Lütfen tekrar giriş yapın.",
                    });
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 2000);
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            console.log("API yanıtı:", responseData);

            // Listeyi API'den yenile
            await fetchReservations();

            setNotification({
                show: true,
                type: "success",
                message: "Yat müsaitliği başarıyla oluşturuldu!",
            });

            // Form'u temizle ve kapat
            setNewReservation({
                yachtId: "",
                yachtName: "",
                startDate: "",
                endDate: "",
                notes: "",
            });
            setFormErrors({});
            setShowAddForm(false);
        } catch (error) {
            console.error("Müsaitlik oluşturma hatası:", error);
            setNotification({
                show: true,
                type: "error",
                message: `Müsaitlik oluşturulurken bir hata oluştu: ${error.message}`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Rezervasyon durumu rengini belirle
    const getStatusColor = (status) => {
        switch (status) {
            case "Aktif":
                return "bg-green-500/20 text-green-400";
            case "Beklemede":
                return "bg-yellow-500/20 text-yellow-400";
            case "Tamamlandı":
                return "bg-blue-500/20 text-blue-400";
            case "İptal":
                return "bg-red-500/20 text-red-400";
            case "Müsait":
                return "bg-emerald-500/20 text-emerald-400";
            default:
                return "bg-gray-500/20 text-gray-400";
        }
    };

    // Filtrelenmiş rezervasyonlar
    const filteredReservations = reservations.filter((reservation) => {
        const matchesSearch =
            reservation.yachtName
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            (reservation.captain && reservation.captain
                .toLowerCase()
                .includes(searchTerm.toLowerCase())) ||
            (reservation.notes && reservation.notes
                .toLowerCase()
                .includes(searchTerm.toLowerCase()));

        const matchesStatus =
            statusFilter === "Tümü" || reservation.status === statusFilter;

        // Tarih filtresi - daha esnek mantık
        let matchesDateRange = true;
        if (dateFilter.startDate || dateFilter.endDate) {
            const reservationStart = new Date(reservation.startDate);
            const reservationEnd = new Date(reservation.endDate);

            // Sadece başlangıç tarihi seçilmişse
            if (dateFilter.startDate && !dateFilter.endDate) {
                const filterStart = new Date(dateFilter.startDate);
                matchesDateRange = reservationEnd >= filterStart;
            }
            // Sadece bitiş tarihi seçilmişse
            else if (!dateFilter.startDate && dateFilter.endDate) {
                const filterEnd = new Date(dateFilter.endDate);
                matchesDateRange = reservationStart <= filterEnd;
            }
            // Her iki tarih de seçilmişse
            else if (dateFilter.startDate && dateFilter.endDate) {
                const filterStart = new Date(dateFilter.startDate);
                const filterEnd = new Date(dateFilter.endDate);
                // Rezervasyon tarihleri seçilen aralıkla çakışıyor mu?
                matchesDateRange =
                    reservationStart <= filterEnd &&
                    reservationEnd >= filterStart;
            }

            // Debug için
            console.log("Tarih filtresi:", {
                reservation: reservation.yachtName,
                reservationStart: reservation.startDate,
                reservationEnd: reservation.endDate,
                filterStart: dateFilter.startDate,
                filterEnd: dateFilter.endDate,
                matches: matchesDateRange,
            });
        }

        return matchesSearch && matchesStatus && matchesDateRange;
    });

    return (
        <AdminLayout currentPage="reservations">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-2xl font-bold">
                                Yat Müsaitlik Yönetimi
                            </h1>
                            <p className="text-white/60">
                                {filteredReservations.length !==
                                reservations.length
                                    ? `${filteredReservations.length} / ${reservations.length} müsaitlik gösteriliyor`
                                    : `Toplam ${reservations.length} yat müsaitliği`}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Müsaitlik ara..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                    className="px-4 py-2 pl-10 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-all duration-300"
                                />
                                <svg
                                    className="w-5 h-5 text-white/30 absolute left-3 top-2.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </div>

                            {/* Modern Tarih Aralığı Seçici */}
                            <button
                                onClick={() => setShowDatePicker(true)}
                                className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl text-white hover:from-blue-600/30 hover:to-purple-600/30 hover:border-blue-400 transition-all duration-300 backdrop-blur-sm"
                            >
                                <div className="flex items-center gap-2">
                                    <svg
                                        className="w-5 h-5 text-blue-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <span className="font-medium">
                                        {dateFilter.startDate ||
                                        dateFilter.endDate
                                            ? `${
                                                  dateFilter.startDate
                                                      ? new Date(
                                                            dateFilter.startDate
                                                        ).toLocaleDateString(
                                                            "tr-TR"
                                                        )
                                                      : "..."
                                              } - ${
                                                  dateFilter.endDate
                                                      ? new Date(
                                                            dateFilter.endDate
                                                        ).toLocaleDateString(
                                                            "tr-TR"
                                                        )
                                                      : "..."
                                              }`
                                            : "Tarih Aralığı Seç"}
                                    </span>
                                </div>
                                {(dateFilter.startDate ||
                                    dateFilter.endDate) && (
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDateFilter({
                                                    startDate: "",
                                                    endDate: "",
                                                });
                                            }}
                                            className="text-white/60 hover:text-white ml-1 hover:bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-sm"
                                            title="Temizle"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </button>

                            <select
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                }
                                className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="Tümü">Tüm Durumlar</option>
                                <option value="Aktif">Aktif</option>
                                <option value="Beklemede">Beklemede</option>
                                <option value="Müsait">Müsait</option>
                                <option value="Tamamlandı">Tamamlandı</option>
                                <option value="İptal">İptal</option>
                            </select>
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Yat Müsaitlik Oluştur
                            </button>
                        </div>
                    </div>

                    {/* Loading Durumu */}
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    )}

                    {/* Hata Durumu */}
                    {error && (
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-8">
                            <p className="text-red-400">{error}</p>
                        </div>
                    )}

                    {/* Rezervasyon Listesi */}
                    {!loading && !error && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-white/60 border-b border-white/10">
                                            <th className="text-left pb-3 font-medium">
                                                Yat
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Kaptan
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Tarih Aralığı
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Notlar
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Durum
                                            </th>
                                            <th className="text-center pb-3 font-medium">
                                                İşlemler
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReservations.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan="6"
                                                    className="text-center py-12 text-white/60"
                                                >
                                                    <p className="text-lg">
                                                        {searchTerm ||
                                                        statusFilter !== "Tümü"
                                                            ? "Arama kriterlerinize uygun müsaitlik bulunamadı"
                                                            : "Henüz yat müsaitliği kaydı bulunmuyor"}
                                                    </p>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredReservations.map(
                                                (reservation) => (
                                                    <tr
                                                        key={reservation.id}
                                                        className="border-b border-white/5 hover:bg-white/5"
                                                    >
                                                        <td className="py-4 font-medium">
                                                            {
                                                                reservation.yachtName
                                                            }
                                                        </td>
                                                        <td className="py-4">
                                                            <div>
                                                                <p className="font-medium">
                                                                    {reservation.captain || "Atanacak"}
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="py-4">
                                                            <div className="text-sm">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-white font-medium">
                                                                        {new Date(
                                                                            reservation.startDate
                                                                        ).toLocaleDateString(
                                                                            "tr-TR"
                                                                        )}
                                                                    </span>
                                                                    <span className="text-white/60">
                                                                        →
                                                                    </span>
                                                                    <span className="text-white font-medium">
                                                                        {new Date(
                                                                            reservation.endDate
                                                                        ).toLocaleDateString(
                                                                            "tr-TR"
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">
                                                                    <svg
                                                                        className="w-3 h-3"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                        />
                                                                    </svg>
                                                                    {Math.ceil(
                                                                        (new Date(
                                                                            reservation.endDate
                                                                        ) -
                                                                            new Date(
                                                                                reservation.startDate
                                                                            )) /
                                                                            (1000 *
                                                                                60 *
                                                                                60 *
                                                                                24)
                                                                    )}{" "}
                                                                    gün
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-4">
                                                            <div className="text-sm text-white/80 max-w-xs">
                                                                {reservation.notes || "Not yok"}
                                                            </div>
                                                        </td>
                                                        <td className="py-4">
                                                            <span
                                                                className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                                                                    reservation.status
                                                                )}`}
                                                            >
                                                                {
                                                                    reservation.status
                                                                }
                                                            </span>
                                                        </td>
                                                        <td className="py-4 text-center">
                                                            <div className="flex justify-center gap-2">
                                                                <button className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                                                                    <svg
                                                                        className="w-4 h-4"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                                <button className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors">
                                                                    <svg
                                                                        className="w-4 h-4"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

            {/* Rezervasyon Ekleme Modal */}
            {showAddForm && createPortal(
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-[9999] p-4 overflow-y-auto">
                    <div className="bg-gray-800/90 backdrop-blur-sm rounded-xl w-full max-w-5xl min-h-fit my-8 border border-white/20">
                        <div className="sticky top-0 bg-gray-800/95 backdrop-blur-sm border-b border-white/10 px-6 py-4 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-white">
                                    Yat Müsaitlik Oluştur
                                </h2>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div>
                                    <label className="block text-sm font-semibold text-white/90 mb-2">
                                        Yat Seçimi *
                                    </label>
                                    <div className="relative">
                                        {loadingYachts ? (
                                            <div className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white/60 flex items-center gap-3">
                                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                                                <span>
                                                    Yatlar yükleniyor...
                                                </span>
                                            </div>
                                        ) : (
                                            <select
                                                value={newReservation.yachtId}
                                                onChange={(e) => {
                                                    const selectedYacht =
                                                        availableYachts.find(
                                                            (yacht) =>
                                                                yacht.id.toString() ===
                                                                e.target.value
                                                        );
                                                    if (selectedYacht) {
                                                        handleYachtSelect(
                                                            selectedYacht
                                                        );
                                                    }
                                                }}
                                                className={`w-full pl-12 pr-10 py-3 bg-white/10 border rounded-xl text-white focus:outline-none focus:ring-2 transition-all duration-300 hover:bg-white/15 appearance-none ${
                                                    formErrors.yachtId
                                                        ? "border-red-500 focus:ring-red-500/50"
                                                        : "border-white/20 focus:ring-blue-500/50 focus:border-blue-400"
                                                }`}
                                                required
                                            >
                                                <option
                                                    value=""
                                                    disabled
                                                    className="bg-gray-800 text-gray-300"
                                                >
                                                    {availableYachts.length > 0
                                                        ? "Yat seçin..."
                                                        : "Size ait yat bulunamadı - Önce yat ekleyin"}
                                                </option>
                                                {availableYachts.map(
                                                    (yacht) => (
                                                        <option
                                                            key={yacht.id}
                                                            value={yacht.id.toString()}
                                                            className="bg-gray-800 text-white py-2"
                                                        >
                                                            {yacht.name} -{" "}
                                                            {getYachtTypeText(yacht.type)} (
                                                            {yacht.length}m)
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        )}

                                        {/* Yat İkonu */}
                                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 pointer-events-none">
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                                                />
                                            </svg>
                                        </div>

                                        {/* Down Arrow İkonu */}
                                        {!loadingYachts && (
                                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/60 pointer-events-none">
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {formErrors.yachtId && (
                                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            {formErrors.yachtId}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-white/90 mb-2">
                                        Başlangıç Tarihi *
                                    </label>
                                    <DatePicker
                                        selected={
                                            newReservation.startDate
                                                ? new Date(
                                                      newReservation.startDate
                                                  )
                                                : null
                                        }
                                        onChange={(date) => {
                                            const dateString = date
                                                ? date
                                                      .toISOString()
                                                      .split("T")[0]
                                                : "";
                                            setNewReservation({
                                                ...newReservation,
                                                startDate: dateString,
                                            });
                                            // Hata mesajını temizle
                                            if (formErrors.startDate) {
                                                setFormErrors({
                                                    ...formErrors,
                                                    startDate: "",
                                                });
                                            }
                                        }}
                                        selectsStart
                                        startDate={
                                            newReservation.startDate
                                                ? new Date(
                                                      newReservation.startDate
                                                  )
                                                : null
                                        }
                                        endDate={
                                            newReservation.endDate
                                                ? new Date(
                                                      newReservation.endDate
                                                  )
                                                : null
                                        }
                                        excludeDates={getExcludedDates()}
                                        placeholderText="Başlangıç Tarihi Seçin"
                                        dateFormat="dd MMMM yyyy, eeee"
                                        locale="tr"
                                        isClearable={true}
                                        showPopperArrow={false}
                                        monthsShown={1}
                                        fixedHeight
                                        popperPlacement="bottom-start"
                                        calendarClassName="rounded-xl border-none shadow-2xl bg-white"
                                        popperClassName="z-[9999]"
                                        customInput={
                                            <div className="relative">
                                                <input
                                                    value={
                                                        newReservation.startDate
                                                            ? formatDate(
                                                                  new Date(
                                                                      newReservation.startDate
                                                                  )
                                                              )
                                                            : ""
                                                    }
                                                    placeholder="Başlangıç Tarihi Seçin"
                                                    className={`w-full rounded-xl py-4 pl-12 pr-5 bg-white/10 border transition-all duration-300 focus:outline-none focus:ring-2 text-white placeholder-white/50 backdrop-blur-sm cursor-pointer hover:bg-white/15 ${
                                                        formErrors.startDate
                                                            ? "border-red-500 focus:ring-red-500/50"
                                                            : "border-white/20 hover:border-blue-400 focus:ring-blue-500/50 focus:border-blue-400"
                                                    }`}
                                                    readOnly
                                                />
                                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 pointer-events-none">
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        }
                                    />
                                    {formErrors.startDate && (
                                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            {formErrors.startDate}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-white/90 mb-2">
                                        Bitiş Tarihi *
                                    </label>
                                    <DatePicker
                                        selected={
                                            newReservation.endDate
                                                ? new Date(
                                                      newReservation.endDate
                                                  )
                                                : null
                                        }
                                        onChange={(date) => {
                                            const dateString = date
                                                ? date
                                                      .toISOString()
                                                      .split("T")[0]
                                                : "";
                                            setNewReservation({
                                                ...newReservation,
                                                endDate: dateString,
                                            });
                                            // Hata mesajını temizle
                                            if (formErrors.endDate) {
                                                setFormErrors({
                                                    ...formErrors,
                                                    endDate: "",
                                                });
                                            }
                                        }}
                                        selectsEnd
                                        startDate={
                                            newReservation.startDate
                                                ? new Date(
                                                      newReservation.startDate
                                                  )
                                                : null
                                        }
                                        endDate={
                                            newReservation.endDate
                                                ? new Date(
                                                      newReservation.endDate
                                                  )
                                                : null
                                        }
                                        excludeDates={getExcludedDates()}
                                        minDate={
                                            newReservation.startDate
                                                ? new Date(
                                                      newReservation.startDate
                                                  )
                                                : null
                                        }
                                        placeholderText="Bitiş Tarihi Seçin"
                                        dateFormat="dd MMMM yyyy, eeee"
                                        locale="tr"
                                        isClearable={true}
                                        showPopperArrow={false}
                                        monthsShown={1}
                                        fixedHeight
                                        popperPlacement="bottom-start"
                                        calendarClassName="rounded-xl border-none shadow-2xl bg-white"
                                        popperClassName="z-[9999]"
                                        customInput={
                                            <div className="relative">
                                                <input
                                                    value={
                                                        newReservation.endDate
                                                            ? formatDate(
                                                                  new Date(
                                                                      newReservation.endDate
                                                                  )
                                                              )
                                                            : ""
                                                    }
                                                    placeholder="Bitiş Tarihi Seçin"
                                                    className={`w-full rounded-xl py-4 pl-12 pr-5 bg-white/10 border transition-all duration-300 focus:outline-none focus:ring-2 text-white placeholder-white/50 backdrop-blur-sm cursor-pointer hover:bg-white/15 ${
                                                        formErrors.endDate
                                                            ? "border-red-500 focus:ring-red-500/50"
                                                            : "border-white/20 hover:border-blue-400 focus:ring-blue-500/50 focus:border-blue-400"
                                                    }`}
                                                    readOnly
                                                />
                                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 pointer-events-none">
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        }
                                    />
                                    {formErrors.endDate && (
                                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            {formErrors.endDate}
                                        </p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-white/90 mb-2">
                                        Notlar *
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={newReservation.notes}
                                        onChange={handleInputChange}
                                        placeholder="Müsaitlik hakkında notlar ekleyin (zorunlu)"
                                        className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 transition-all duration-300 hover:bg-white/15 resize-none ${
                                            formErrors.notes
                                                ? "border-red-500 focus:ring-red-500/50"
                                                : "border-white/20 focus:ring-blue-500/50 focus:border-blue-400"
                                        }`}
                                        rows="3"
                                        required
                                    />
                                    {formErrors.notes && (
                                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            {formErrors.notes}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-4 border-t border-white/10">
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="px-6 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/15 transition-colors font-medium"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed font-medium min-w-[140px]"
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            <span>Ekleniyor...</span>
                                        </div>
                                    ) : (
                                        "Müsaitlik Oluştur"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* Modern Tarih Seçici Modal */}
            {showDatePicker && createPortal(
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                    <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl max-w-4xl w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-white/10">
                            <div>
                                <h3 className="text-2xl font-bold text-white">
                                    Tarih Aralığı Seçin
                                </h3>
                                <p className="text-white/60 mt-1">
                                    Filtrelemek istediğiniz tarih aralığını
                                    belirleyin
                                </p>
                            </div>
                            <button
                                onClick={() => setShowDatePicker(false)}
                                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Takvimler */}
                        <div className="px-6 pb-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Başlangıç Tarihi */}
                                <div className="space-y-3">
                                    <div className="text-center">
                                        <h4 className="text-lg font-semibold text-white mb-3">
                                            Başlangıç Tarihi
                                        </h4>
                                        <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-2.5 mb-3">
                                            <p className="text-blue-300 text-sm font-medium">
                                                {dateFilter.startDate
                                                    ? new Date(
                                                          dateFilter.startDate
                                                      ).toLocaleDateString(
                                                          "tr-TR",
                                                          {
                                                              weekday: "long",
                                                              year: "numeric",
                                                              month: "long",
                                                              day: "numeric",
                                                          }
                                                      )
                                                    : "Başlangıç tarihi seçin"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex justify-center">
                                        <DatePicker
                                            selected={
                                                dateFilter.startDate
                                                    ? new Date(
                                                          dateFilter.startDate
                                                      )
                                                    : null
                                            }
                                            onChange={(date) => {
                                                const dateString = date
                                                    ? date
                                                          .toISOString()
                                                          .split("T")[0]
                                                    : "";
                                                setDateFilter({
                                                    ...dateFilter,
                                                    startDate: dateString,
                                                });
                                            }}
                                            selectsStart
                                            startDate={
                                                dateFilter.startDate
                                                    ? new Date(
                                                          dateFilter.startDate
                                                      )
                                                    : null
                                            }
                                            endDate={
                                                dateFilter.endDate
                                                    ? new Date(
                                                          dateFilter.endDate
                                                      )
                                                    : null
                                            }
                                            locale="tr"
                                            inline
                                            monthsShown={1}
                                            calendarClassName="bg-white rounded-xl shadow-lg border-0"
                                        />
                                    </div>
                                </div>

                                {/* Bitiş Tarihi */}
                                <div className="space-y-3">
                                    <div className="text-center">
                                        <h4 className="text-lg font-semibold text-white mb-3">
                                            Bitiş Tarihi
                                        </h4>
                                        <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-2.5 mb-3">
                                            <p className="text-purple-300 text-sm font-medium">
                                                {dateFilter.endDate
                                                    ? new Date(
                                                          dateFilter.endDate
                                                      ).toLocaleDateString(
                                                          "tr-TR",
                                                          {
                                                              weekday: "long",
                                                              year: "numeric",
                                                              month: "long",
                                                              day: "numeric",
                                                          }
                                                      )
                                                    : "Bitiş tarihi seçin"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex justify-center">
                                        <DatePicker
                                            selected={
                                                dateFilter.endDate
                                                    ? new Date(
                                                          dateFilter.endDate
                                                      )
                                                    : null
                                            }
                                            onChange={(date) => {
                                                const dateString = date
                                                    ? date
                                                          .toISOString()
                                                          .split("T")[0]
                                                    : "";
                                                setDateFilter({
                                                    ...dateFilter,
                                                    endDate: dateString,
                                                });
                                            }}
                                            selectsEnd
                                            startDate={
                                                dateFilter.startDate
                                                    ? new Date(
                                                          dateFilter.startDate
                                                      )
                                                    : null
                                            }
                                            endDate={
                                                dateFilter.endDate
                                                    ? new Date(
                                                          dateFilter.endDate
                                                      )
                                                    : null
                                            }
                                            minDate={
                                                dateFilter.startDate
                                                    ? new Date(
                                                          dateFilter.startDate
                                                      )
                                                    : null
                                            }
                                            locale="tr"
                                            inline
                                            monthsShown={1}
                                            calendarClassName="bg-white rounded-xl shadow-lg border-0"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Alt Butonlar */}
                            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                                <div className="text-white/60 text-sm">
                                    {dateFilter.startDate &&
                                        dateFilter.endDate && (
                                            <span>
                                                Seçilen aralık:{" "}
                                                {Math.ceil(
                                                    (new Date(
                                                        dateFilter.endDate
                                                    ) -
                                                        new Date(
                                                            dateFilter.startDate
                                                        )) /
                                                        (1000 * 60 * 60 * 24)
                                                ) + 1}{" "}
                                                gün
                                            </span>
                                        )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() =>
                                            setDateFilter({
                                                startDate: "",
                                                endDate: "",
                                            })
                                        }
                                        className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                                    >
                                        Temizle
                                    </button>
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-colors font-medium"
                                    >
                                        Uygula
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Bildirim */}
            {notification.show && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-xl shadow-lg ${
                        notification.type === "success"
                            ? "bg-green-500"
                            : "bg-red-500"
                    } text-white z-[10000]`}
                >
                    {notification.message}
                </div>
            )}
        </AdminLayout>
    );
}

export default ReservationManagement;
